
import { useState, useEffect, useCallback } from 'react';
import { trackEvent } from '@/utils/analytics';

type Theme = 'light' | 'dark' | 'auto';
type ActualTheme = 'light' | 'dark';

interface ThemePreferences {
  theme: Theme;
  actualTheme: ActualTheme;
  systemPreference: ActualTheme;
  userHasManuallySet: boolean;
}

export const useAdvancedTheme = () => {
  const [preferences, setPreferences] = useState<ThemePreferences>({
    theme: 'auto',
    actualTheme: 'dark',
    systemPreference: 'dark',
    userHasManuallySet: false
  });

  // Detect system preference
  const getSystemPreference = useCallback((): ActualTheme => {
    if (typeof window === 'undefined') return 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }, []);

  // Load saved preferences
  useEffect(() => {
    const saved = localStorage.getItem('theme-preferences');
    const systemPref = getSystemPreference();
    
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const actualTheme = parsed.theme === 'auto' ? systemPref : parsed.theme;
        
        setPreferences({
          theme: parsed.theme,
          actualTheme,
          systemPreference: systemPref,
          userHasManuallySet: parsed.userHasManuallySet || false
        });
      } catch {
        // Fallback to auto mode
        setPreferences(prev => ({
          ...prev,
          theme: 'auto',
          actualTheme: systemPref,
          systemPreference: systemPref
        }));
      }
    } else {
      setPreferences(prev => ({
        ...prev,
        actualTheme: systemPref,
        systemPreference: systemPref
      }));
    }
  }, [getSystemPreference]);

  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      const newSystemPref: ActualTheme = e.matches ? 'dark' : 'light';
      
      setPreferences(prev => {
        const newActualTheme = prev.theme === 'auto' ? newSystemPref : prev.actualTheme;
        return {
          ...prev,
          systemPreference: newSystemPref,
          actualTheme: newActualTheme
        };
      });
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Apply theme to document
  useEffect(() => {
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(preferences.actualTheme);
    
    // Update meta theme-color
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', 
        preferences.actualTheme === 'dark' ? '#1a1a1a' : '#ffffff'
      );
    }
  }, [preferences.actualTheme]);

  // Set theme function
  const setTheme = useCallback((newTheme: Theme) => {
    const systemPref = getSystemPreference();
    const actualTheme = newTheme === 'auto' ? systemPref : newTheme;
    
    const newPreferences = {
      theme: newTheme,
      actualTheme,
      systemPreference: systemPref,
      userHasManuallySet: newTheme !== 'auto'
    };

    setPreferences(newPreferences);
    localStorage.setItem('theme-preferences', JSON.stringify(newPreferences));
    
    // Track theme change
    trackEvent('theme_change', {
      new_theme: newTheme,
      actual_theme: actualTheme,
      manually_set: newTheme !== 'auto'
    });
  }, [getSystemPreference]);

  // Smart theme suggestion based on time
  const getSuggestedTheme = useCallback((): Theme => {
    const hour = new Date().getHours();
    if (hour >= 6 && hour < 18) {
      return 'light';
    }
    return 'dark';
  }, []);

  // Auto-adjust based on time (if user hasn't manually set)
  useEffect(() => {
    if (!preferences.userHasManuallySet && preferences.theme === 'auto') {
      const suggested = getSuggestedTheme();
      const currentHour = new Date().getHours();
      
      // Suggest theme change at dawn (6am) and dusk (6pm)
      if (currentHour === 6 || currentHour === 18) {
        // Theme suggestion available but not shown to avoid interrupting user
      }
    }
  }, [preferences.userHasManuallySet, preferences.theme, getSuggestedTheme]);

  return {
    ...preferences,
    setTheme,
    getSuggestedTheme,
    isDark: preferences.actualTheme === 'dark',
    isLight: preferences.actualTheme === 'light',
    isAuto: preferences.theme === 'auto'
  };
};
